import { useMemo } from 'react'
import { useSelector } from 'react-redux'
import { selectAuthData } from '@/containers/Login/AuthSelectors'
import { modulesConfig } from '@/containers/SubscriptionFormPageNew/config'
import {
  selectAdditionalContactSubscriptionData,
  selectContactConfig,
  selectIdentityContactSubscriptionData,
  selectPersonalContactSubscriptionData,
  selectPreferencesContactSubscriptionData,
  selectProfessionalContactSubscriptionData,
} from '@/containers/SubscriptionFormPageNew/SubscriptionForm/SubscriptionFormSelectors'
import { IS_PROFESSIONAL } from '@/containers/SubscriptionFormPageNew/SubscriptionForm/Common/constants'
import useFormStatus from '@/containers/SubscriptionFormPageNew/SubscriptionForm/useFormStatus'
import useAddress from '@/containers/SubscriptionFormPageNew/Components/Address/useAddress'
import { countFilledConsumptionFields, missingAddressFields, missingIdentityFields } from './clientTabStatus'
import type { ClientTabField, CustomerRules } from './clientTabStatus'
import type { ClientTabRequirements } from './clientTabRequirementsContext'

const useClientTabRequirements = (): ClientTabRequirements => {
  const { country } = useSelector(selectAuthData)
  const customerRules: CustomerRules = modulesConfig[country as keyof typeof modulesConfig]?.customer ?? {}
  const { formIsLoaded, zohoIsInValid } = useFormStatus()
  const { isValidZohoId } = useSelector(selectContactConfig)
  const personal = useSelector(selectPersonalContactSubscriptionData)
  const { dataCollectionAuth, clientType } = useSelector(selectPreferencesContactSubscriptionData)
  const { siret, socialReason } = useSelector(selectProfessionalContactSubscriptionData)
  const { referral, referralFirstName, referralLastName } = useSelector(selectAdditionalContactSubscriptionData)
  const { taxIdCode } = useSelector(selectIdentityContactSubscriptionData) ?? {}
  const address = useAddress()

  const consent = !!dataCollectionAuth
  const missingIdentity = missingIdentityFields(
    {
      ...personal,
      consent,
      isProfessional: clientType === IS_PROFESSIONAL,
      clientType,
      siret,
      socialReason,
      taxIdCode,
      referral,
      referralFirstName,
      referralLastName,
    },
    customerRules,
  )
  const missingAddress = missingAddressFields({
    hasAddressesButNoneSelected: !address.activeAddress && address.addressesList.length > 0,
    zipcode: address.zipcode,
    city: address.city,
    residenceType: address.residenceType,
    housingType: address.housingType,
    tenantType: address.tenantType,
    isResident: address.isResident,
    showResidenceType: address.showResidenceType,
    showHousingType: address.houseTypeOptionsArray.length > 0,
    showTenantType: address.tenantTypeOptionsArray.length > 0,
    showIsResident: address.showIsResident,
    optionalResidenceFields: address.optionalResidenceFields,
  })
  const filledConsumption = countFilledConsumptionFields([
    address.heatingEnergy,
    address.hotwaterEnergy,
    address.cookingEnergy,
    address.surface,
  ])
  const missingFieldsKey = [...(consent ? missingIdentity : []), ...missingAddress].join(',')
  const missingFields = useMemo(
    () => new Set((missingFieldsKey ? missingFieldsKey.split(',') : []) as ClientTabField[]),
    [missingFieldsKey],
  )
  const missingIdentityCount = consent ? missingIdentity.length : 1

  return useMemo(
    () => ({
      formIsLoaded,
      zohoIsInValid,
      isValidZohoId,
      missingIdentityCount,
      missingAddressCount: missingAddress.length,
      filledConsumption,
      showConsumption: address.showConsumption,
      missingFields,
    }),
    [
      formIsLoaded,
      zohoIsInValid,
      isValidZohoId,
      missingIdentityCount,
      missingAddress.length,
      filledConsumption,
      address.showConsumption,
      missingFields,
    ],
  )
}

export default useClientTabRequirements
